import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { BauenLogoFull } from "@/components/BauenLogo";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const { login, verify2FA } = useAuth();
  const [email, setEmail] = useState(localStorage.getItem("bauen_remember_email") || "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(1);
  const [remember, setRemember] = useState(!!localStorage.getItem("bauen_remember_email"));
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (step === 1) {
        const res = await login(email, password);
        if (res.requires_2fa) {
          setStep(2);
          toast.success("Código enviado para o seu e-mail!");
        } else {
          if (remember) localStorage.setItem("bauen_remember_email", email);
          else localStorage.removeItem("bauen_remember_email");
          toast.success(`Bem-vindo(a), ${res.name}`);
          navigate("/", { replace: true });
        }
      } else {
        const u = await verify2FA(email, code);
        if (remember) localStorage.setItem("bauen_remember_email", email);
        else localStorage.removeItem("bauen_remember_email");
        toast.success(`Bem-vindo(a), ${u.name}`);
        navigate("/", { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no login");
      if (err.response?.data?.detail?.includes("Muitas tentativas") || err.response?.data?.detail?.includes("expirado")) {
        setStep(1);
        setCode("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/9458996/pexels-photo-9458996.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940"
          alt="Arquitetura moderna BAUEN"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2E3B]/95 via-[#0A2E3B]/60 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end h-full p-12 text-white">
          <div className="max-w-md">
            <h1 className="font-heading text-4xl font-bold tracking-tight leading-tight">
              Onde cada projeto encontra sua estrutura.
            </h1>
            <p className="mt-4 text-white/80 text-sm leading-relaxed">
              Portal exclusivo BAUEN para acompanhamento de documentos, aprovações
              e evolução das suas obras — com segurança e transparência em tempo real.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="mb-10">
            <BauenLogoFull />
          </div>
          <h2 className="font-heading text-2xl font-semibold text-foreground">Acessar sua conta</h2>
          <p className="mt-1 text-sm text-muted-foreground">Entre com suas credenciais BAUEN.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            {step === 1 ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input" placeholder="voce@empresa.com" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Senha</Label>
                    <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                      Esqueceu sua senha?
                    </Link>
                  </div>
                  <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input" placeholder="••••••••" />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="remember" checked={remember} onCheckedChange={setRemember} data-testid="login-remember-checkbox" />
                  <Label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">Lembrar meu acesso</Label>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-11" data-testid="login-submit-btn">
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Entrando…</> : "Entrar"}
                </Button>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="code">Código de Segurança (2FA)</Label>
                    <button type="button" onClick={() => { setStep(1); setCode(""); }} className="text-xs text-primary hover:underline">
                      Voltar
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">
                    Enviamos um código de 6 dígitos para o e-mail: <strong>{email}</strong>
                  </p>
                  <Input id="code" type="text" required value={code} onChange={(e) => setCode(e.target.value)}
                    placeholder="000000" className="text-center text-2xl tracking-widest font-mono h-14" maxLength={6} />
                </div>
                <Button type="submit" disabled={loading || code.length < 6} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-11 mt-4">
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verificando…</> : "Verificar e Entrar"}
                </Button>
              </>
            )}
          </form>

          <p className="mt-8 text-center text-xs text-muted-foreground">
            Portal exclusivo BAUEN · Projeto e Gestão de Obras
          </p>
        </div>
      </div>
    </div>
  );
}
